import React from "react";
//@ts-ignore
import FeatherIcon from "feather-icons-react";
import { Box, Button, CircularProgress, Collapse, IconButton, Stack, Typography, alpha } from "@mui/material";
import { notificationAPI } from "../../../api/notificationAPI";
import { enablePush, isPushSupported } from "../../../helpers/webPush";
import { useAuth } from "../../../contexts/AuthProvider/AuthProvider";
import { useDispatchWrapper } from "../../../hooks";
import { showMessage } from "../../../redux/messages/messagesActions";

// "Later" hides the banner for a few days rather than forever.
const SNOOZE_KEY = "notification_push_prompt_snooze_until";
const SNOOZE_MS = 3 * 24 * 60 * 60 * 1000;
const SHOW_DELAY_MS = 1200;

const isSnoozed = (): boolean => {
  try {
    const until = Number(localStorage.getItem(SNOOZE_KEY) || 0);
    return until > Date.now();
  } catch {
    return false;
  }
};

const snooze = (ms: number = SNOOZE_MS) => {
  try {
    localStorage.setItem(SNOOZE_KEY, String(Date.now() + ms));
  } catch {
    // Storage blocked (private mode etc.) — the banner just comes back next load.
  }
};

// Asks once after login whether to turn on browser push. Only shows when the
// browser has never been asked (`default`) — a granted browser is registered
// silently on login, and a denied one can't be prompted again.
export const PushPermissionBanner: React.FC = () => {
  const { isLogin, user } = useAuth();
  const dispatch = useDispatchWrapper();
  const [visible, setVisible] = React.useState(false);
  const [enabling, setEnabling] = React.useState(false);

  React.useEffect(() => {
    if (!isLogin || !isPushSupported()) return;
    if (Notification.permission !== "default" || isSnoozed()) return;

    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        // No VAPID keys on the backend → push can't work, so don't offer it.
        const res = await notificationAPI.getVapidPublicKey();
        if (!cancelled && res.data?.configured) setVisible(true);
      } catch (error) {
        console.error("Failed to check push configuration:", error);
      }
    }, SHOW_DELAY_MS);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [isLogin]);

  const handleLater = () => {
    snooze();
    setVisible(false);
  };

  const handleEnable = async () => {
    setEnabling(true);
    try {
      const res = await enablePush(user?.userUuid);
      if (res.ok) {
        dispatch(
          showMessage({
            type: "success",
            message: "You're all set — we'll notify you even when this tab is closed.",
            displayAs: "snackbar",
          }),
        );
        setVisible(false);
        return;
      }
      if (res.reason === "denied") {
        dispatch(
          showMessage({
            type: "warning",
            message: "Notifications are blocked. You can allow them from your browser's site settings.",
            displayAs: "snackbar",
          }),
        );
        setVisible(false);
      } else {
        // The browser prompt was dismissed without a choice — ask again tomorrow.
        snooze(24 * 60 * 60 * 1000);
        setVisible(false);
      }
    } catch (error) {
      console.error("Failed to enable push notifications:", error);
      dispatch(
        showMessage({
          type: "error",
          message: "Couldn't turn on notifications. Please try again.",
          displayAs: "snackbar",
        }),
      );
    } finally {
      setEnabling(false);
    }
  };

  return (
    <Collapse in={visible} unmountOnExit>
      <Box
        sx={(theme) => ({
          position: "relative",
          mb: 2,
          borderRadius: 3,
          overflow: "hidden",
          backgroundColor: theme.palette.background.paper,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
          boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.08)}`,
        })}
      >
        {/* Brand accent strip: navy into brand red */}
        <Box
          sx={(theme) => ({
            position: "absolute",
            left: 0,
            top: 0,
            bottom: 0,
            width: 5,
            background: `linear-gradient(180deg, ${theme.palette.primary.main} 0%, ${theme.palette.error.main} 100%)`,
          })}
        />
        {/* Soft decorative wash on the right, kept faint so the card stays calm */}
        <Box
          sx={(theme) => ({
            position: "absolute",
            right: -70,
            top: -90,
            width: 260,
            height: 260,
            borderRadius: "50%",
            background: `radial-gradient(circle, ${alpha(theme.palette.primary.main, 0.07)} 0%, transparent 70%)`,
            pointerEvents: "none",
          })}
        />

        <Stack
          direction={{ xs: "column", md: "row" }}
          alignItems={{ xs: "flex-start", md: "center" }}
          spacing={2}
          sx={{ position: "relative", p: { xs: 2, sm: 2.5 }, pl: { xs: 2.5, sm: 3 }, pr: { xs: 5, md: 6 } }}
        >
          <Box
            sx={(theme) => ({
              position: "relative",
              width: 52,
              height: 52,
              flexShrink: 0,
              borderRadius: "14px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#fff",
              backgroundColor: theme.palette.primary.main,
              boxShadow: `0 6px 14px ${alpha(theme.palette.primary.main, 0.3)}`,
            })}
          >
            <FeatherIcon icon="bell" width="24" height="24" />
            {/* Unread-style dot */}
            <Box
              sx={(theme) => ({
                position: "absolute",
                top: 10,
                right: 11,
                width: 9,
                height: 9,
                borderRadius: "50%",
                backgroundColor: theme.palette.error.main,
                border: `2px solid ${theme.palette.primary.main}`,
              })}
            />
          </Box>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="h4" fontWeight={700} color="primary.main">
              Never miss an update
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, maxWidth: 620 }}>
              Turn on desktop notifications to hear about new leads, bookings and payments the
              moment they happen — even when this tab is closed.
            </Typography>
            <Stack direction="row" spacing={1} sx={{ mt: 1.25, flexWrap: "wrap", rowGap: 1 }}>
              {[
                { icon: "user-plus", label: "New leads" },
                { icon: "calendar", label: "Bookings" },
                { icon: "credit-card", label: "Payments" },
              ].map((chip) => (
                <Stack
                  key={chip.label}
                  direction="row"
                  alignItems="center"
                  spacing={0.75}
                  sx={(theme) => ({
                    px: 1.25,
                    py: 0.4,
                    borderRadius: "999px",
                    fontSize: 12,
                    fontWeight: 600,
                    color: theme.palette.primary.main,
                    backgroundColor: alpha(theme.palette.primary.main, 0.07),
                  })}
                >
                  <FeatherIcon icon={chip.icon} width="13" height="13" />
                  <span>{chip.label}</span>
                </Stack>
              ))}
            </Stack>
          </Box>

          <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
            <Button
              variant="text"
              color="inherit"
              onClick={handleLater}
              disabled={enabling}
              sx={{ color: "text.secondary", fontWeight: 600 }}
            >
              Later
            </Button>
            <Button
              variant="contained"
              onClick={handleEnable}
              disabled={enabling}
              startIcon={
                enabling ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <FeatherIcon icon="bell" width="16" height="16" />
                )
              }
              sx={(theme) => ({
                fontWeight: 700,
                color: "#fff",
                backgroundColor: theme.palette.primary.main,
                boxShadow: "none",
                "&:hover": {
                  // primary.dark is orange in some themes, so darken the main colour instead.
                  backgroundColor: alpha(theme.palette.primary.main, 0.88),
                  boxShadow: "none",
                },
                "&.Mui-disabled": { backgroundColor: alpha(theme.palette.primary.main, 0.6), color: "#fff" },
              })}
            >
              {enabling ? "Enabling…" : "Enable notifications"}
            </Button>
          </Stack>
        </Stack>

        <IconButton
          aria-label="Remind me later"
          onClick={handleLater}
          size="small"
          sx={{ position: "absolute", top: 8, right: 8, color: "text.secondary" }}
        >
          <FeatherIcon icon="x" width="18" height="18" />
        </IconButton>
      </Box>
    </Collapse>
  );
};
